/**
 * Team-only panels of the client team console (Enterprise teams): per-rep
 * funnel, approval queue, booked calls, account health, prospect
 * ownership + do-not-contact, and the compliance export. Data comes from
 * the leads worker's /api/team/* routes, scoped to the manager's own org.
 */

import { useState } from 'react'
import { CalendarCheck, Check, Download, ExternalLink, Filter, Loader2, Search, UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { fmtLocale } from '@/lib/dates'
import { OverviewPage } from '@/features/admin/super-admin'
import { useAdminOverview, useAdminUsers } from '@/features/admin/use-admin'
import { localDateStamp } from '@/features/leads/export-helpers'
import {
  useExportTeamMessages,
  useTeamApprovalAction,
  useTeamApprovals,
  useTeamBookings,
  useTeamDoNotContact,
  useTeamHealth,
  useTeamOwnership,
  type FunnelCounts,
  type TeamRepFunnel,
} from './use-team'

const MUTED = 'text-xs text-[var(--color-silver)]'
const NEUTRAL_BADGE = 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver-bright)] text-[10px]'
const GOOD_BADGE = 'bg-[rgba(16,185,129,0.12)] text-[#34D399] border-[rgba(16,185,129,0.25)] text-[10px]'
const WARN_BADGE = 'bg-[rgba(245,158,11,0.12)] text-[#FBBF24] border-[rgba(245,158,11,0.3)] text-[10px]'

const when = (v: string | null | undefined) => fmtLocale(v, 'datetime', '-')

function EmptyRow({ cols, children }: { cols: number; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={cols} className="py-6 text-center text-sm text-[var(--color-silver)]">
        {children}
      </TableCell>
    </TableRow>
  )
}

function RepFilter({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const { data: users } = useAdminUsers()
  return (
    <div className="flex items-center gap-2">
      <Filter className="h-3.5 w-3.5 text-[var(--color-silver)]" />
      <Select value={value ?? '__all__'} onValueChange={(v) => onChange(v === '__all__' ? null : v)}>
        <SelectTrigger className="h-8 w-full text-xs sm:w-64">
          <SelectValue placeholder="Filter by rep" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All reps</SelectItem>
          {(users ?? []).map((u) => (
            <SelectItem key={u.id} value={u.id}>
              {u.full_name || u.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Overview: the shared console overview plus the per-rep funnel.
// ---------------------------------------------------------------------------

const FUNNEL_COLS: Array<{ key: keyof FunnelCounts; label: string }> = [
  { key: 'invites_sent', label: 'Invites' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'replies', label: 'Replies' },
  { key: 'calls_booked', label: 'Calls booked' },
]

function FunnelCell({ now, before }: { now: number; before: number }) {
  return (
    <TableCell className="text-right tabular-nums">
      <span className="text-[var(--color-silver-bright)]">{now}</span>
      <span className={`ml-1 ${MUTED}`}>/ {before}</span>
    </TableCell>
  )
}

export function RepFunnelCard({ funnel }: { funnel: TeamRepFunnel }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Per rep: this week / last week</CardTitle>
        <p className={MUTED}>
          Rolling 7-day weeks. Accepted counts that week&apos;s invites that have since been accepted.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rep</TableHead>
              {FUNNEL_COLS.map((c) => (
                <TableHead key={c.key} className="text-right">
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {funnel.reps.map((r) => (
              <TableRow key={r.user_id}>
                <TableCell className="text-sm text-[var(--color-silver-bright)]">{r.full_name || r.email}</TableCell>
                {FUNNEL_COLS.map((c) => (
                  <FunnelCell key={c.key} now={r.this_week[c.key]} before={r.last_week[c.key]} />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="text-sm font-semibold text-[var(--color-silver-bright)]">Team total</TableCell>
              {FUNNEL_COLS.map((c) => (
                <FunnelCell key={c.key} now={funnel.total.this_week[c.key]} before={funnel.total.last_week[c.key]} />
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export function TeamOverviewPage() {
  // Same query (and cache entry) OverviewPage reads, so this costs no extra request.
  const { data } = useAdminOverview()
  return (
    <div className="space-y-6">
      {data?.rep_funnel && <RepFunnelCard funnel={data.rep_funnel} />}
      <OverviewPage />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Approval queue
// ---------------------------------------------------------------------------

export function ApprovalsPage() {
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const { data: rows, isLoading } = useTeamApprovals(filterUser)
  const action = useTeamApprovalAction()
  const [notice, setNotice] = useState<{ id: string; text: string; ok: boolean } | null>(null)

  const run = (id: string, kind: 'approve' | 'handoff') => {
    setNotice(null)
    action.mutate(
      { id, action: kind },
      {
        onSuccess: () => setNotice({ id, ok: true, text: kind === 'approve' ? 'Sent.' : 'Handed to the rep. It stays in their queue.' }),
        onError: (err) => setNotice({ id, ok: false, text: err instanceof Error ? err.message : 'Something went wrong' }),
      },
    )
  }

  return (
    <div className="space-y-3">
      <RepFilter value={filterUser} onChange={setFilterUser} />
      <p className={MUTED}>
        Messages held for review across your team. Approve sends it now with the rep&apos;s own limits and sending hours.
        Hand to rep leaves it held and notifies them.
      </p>
      {isLoading && (
        <div className="flex items-center py-6 text-sm text-[var(--color-silver)]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading...
        </div>
      )}
      {!isLoading && (rows ?? []).length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-[var(--color-silver)]">Nothing is waiting for approval.</CardContent>
        </Card>
      )}
      {(rows ?? []).map((r) => {
        const busy = action.isPending && action.variables?.id === r.id
        return (
          <Card key={r.id}>
            <CardContent className="space-y-3 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--color-silver-bright)]">{r.lead_name ?? 'Unknown lead'}</span>
                {r.lead_company && <span className={MUTED}>{r.lead_company}</span>}
                {r.lead_linkedin_url && (
                  <a href={r.lead_linkedin_url} target="_blank" rel="noreferrer" className="text-[#34D399] hover:underline" aria-label="LinkedIn profile">
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <Badge className={r.hold_reason === 'review' ? NEUTRAL_BADGE : WARN_BADGE}>
                  {r.hold_reason === 'review' ? 'Held for review' : 'Send failed, needs a push'}
                </Badge>
                {r.handed_to_rep_at && <Badge className={GOOD_BADGE}>Handed to rep</Badge>}
              </div>
              <div className={MUTED}>
                Rep: {r.owner_name || r.owner_email} · {r.message_type.replace(/_/g, ' ')}
                {r.campaign_name ? ` · ${r.campaign_name}` : ''} · {when(r.created_at)}
              </div>
              <p className="whitespace-pre-wrap rounded-md border border-border bg-[rgba(255,255,255,0.02)] p-3 text-sm text-[var(--color-silver-bright)]">
                {r.content}
              </p>
              {r.qg_feedback && (
                <p className={MUTED}>
                  Why it was held{typeof r.qg_score === 'number' ? ` (score ${r.qg_score}/10)` : ''}: {r.qg_feedback}
                </p>
              )}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button size="sm" disabled={busy} onClick={() => run(r.id, 'approve')}>
                  {busy && action.variables?.action === 'approve' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
                  Approve and send
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(r.id, 'handoff')}>
                  <UserCheck className="mr-1 h-3.5 w-3.5" />
                  Hand to rep
                </Button>
              </div>
              {notice?.id === r.id && <p className={`text-xs ${notice.ok ? 'text-[#34D399]' : 'text-[var(--color-danger)]'}`}>{notice.text}</p>}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Booked calls
// ---------------------------------------------------------------------------

const BOOKING_LABELS: Record<string, string> = {
  booked: 'Booked',
  link_sent: 'Link sent',
  proposed: 'Times offered',
  awaiting_email: 'Waiting for email',
  failed: 'Failed',
}

export function BookingsPage() {
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const { data: rows, isLoading } = useTeamBookings(filterUser)
  return (
    <div className="space-y-3">
      <RepFilter value={filterUser} onChange={setFilterUser} />
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>With</TableHead>
                <TableHead>Rep</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Meeting link</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <EmptyRow cols={5}>Loading...</EmptyRow>}
              {!isLoading && (rows ?? []).length === 0 && <EmptyRow cols={5}>No calls booked yet.</EmptyRow>}
              {(rows ?? []).map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <div className="text-sm font-medium text-[var(--color-silver-bright)]">{b.lead_name ?? 'Unknown lead'}</div>
                    <div className={MUTED}>{[b.lead_company, b.invitee_email].filter(Boolean).join(' · ')}</div>
                  </TableCell>
                  <TableCell className={MUTED}>{b.owner_name || b.owner_email}</TableCell>
                  <TableCell className="text-xs text-[var(--color-silver-bright)]">
                    {b.chosen_slot ? when(b.chosen_slot) : '-'}
                    {b.timezone && <div className={MUTED}>{b.timezone}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge className={b.status === 'booked' ? GOOD_BADGE : b.status === 'failed' ? WARN_BADGE : NEUTRAL_BADGE}>
                      {BOOKING_LABELS[b.status] ?? b.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {b.meet_url ? (
                      <a href={b.meet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#34D399] hover:underline">
                        <CalendarCheck className="h-3 w-3" /> Join
                      </a>
                    ) : (
                      <span className={MUTED}>-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Account health
// ---------------------------------------------------------------------------

const TIER_LABELS: Record<string, string> = { free: 'Free', premium: 'Premium', sales_navigator: 'Sales Navigator', recruiter: 'Recruiter' }

export function HealthPage() {
  const { data: reps, isLoading } = useTeamHealth()
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rep</TableHead>
              <TableHead>LinkedIn</TableHead>
              <TableHead className="text-right">Sends today</TableHead>
              <TableHead>Last send</TableHead>
              <TableHead>Pauses</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <EmptyRow cols={5}>Loading...</EmptyRow>}
            {(reps ?? []).map((r) => (
              <TableRow key={r.user_id}>
                <TableCell>
                  <div className="text-sm font-medium text-[var(--color-silver-bright)]">{r.full_name || r.email}</div>
                  {r.linkedin?.name && <div className={MUTED}>{r.linkedin.name}</div>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Badge className={r.linkedin?.status === 'connected' ? GOOD_BADGE : WARN_BADGE}>
                      {r.linkedin ? (r.linkedin.status === 'connected' ? 'Connected' : 'Needs reconnect') : 'Not connected'}
                    </Badge>
                    {r.linkedin?.tier && <Badge className={NEUTRAL_BADGE}>{TIER_LABELS[r.linkedin.tier] ?? r.linkedin.tier}</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  <span className={r.sends_today >= r.daily_cap ? 'text-[#FBBF24]' : 'text-[var(--color-silver-bright)]'}>{r.sends_today}</span>
                  <span className={`ml-1 ${MUTED}`}>/ {r.daily_cap}</span>
                </TableCell>
                <TableCell className={MUTED}>{when(r.last_send_at)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.rate_limited_since && <Badge className={WARN_BADGE}>LinkedIn rate limit, paused since {when(r.rate_limited_since)}</Badge>}
                    {r.invites_paused_since && <Badge className={WARN_BADGE}>Invites paused since {when(r.invites_paused_since)}</Badge>}
                    {!r.rate_limited_since && !r.invites_paused_since && <span className={MUTED}>None</span>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className={`px-4 pb-3 pt-2 ${MUTED}`}>Sends today counts messages sent or held today (UTC day), against the rep&apos;s plan cap.</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Prospect ownership + do-not-contact (read only)
// ---------------------------------------------------------------------------

export function OwnershipPage() {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useTeamOwnership(search)
  const { data: dnc } = useTeamDoNotContact()
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Search className="h-3.5 w-3.5 text-[var(--color-silver)]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search prospect, company or rep"
          className="h-8 w-full text-xs sm:w-80"
        />
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Who owns which prospect</CardTitle>
          <p className={MUTED}>
            Each person can be worked by one rep on your team. {data ? `${data.total} prospects claimed.` : ''}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prospect</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Claimed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <EmptyRow cols={4}>Loading...</EmptyRow>}
              {!isLoading && (data?.claims ?? []).length === 0 && <EmptyRow cols={4}>No matches.</EmptyRow>}
              {(data?.claims ?? []).map((cl) => (
                <TableRow key={cl.profile_key}>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm font-medium text-[var(--color-silver-bright)]">
                      {cl.lead_name ?? cl.profile_key}
                      {cl.linkedin_url && (
                        <a href={cl.linkedin_url} target="_blank" rel="noreferrer" className="text-[#34D399]" aria-label="LinkedIn profile">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    {cl.company && <div className={MUTED}>{cl.company}</div>}
                  </TableCell>
                  <TableCell className={MUTED}>{cl.owner_name || cl.owner_email || 'Former rep'}</TableCell>
                  <TableCell>{cl.lead_status ? <Badge className={NEUTRAL_BADGE}>{cl.lead_status}</Badge> : <span className={MUTED}>-</span>}</TableCell>
                  <TableCell className={MUTED}>{fmtLocale(cl.claimed_at, 'date', '-')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Do not contact</CardTitle>
          <p className={MUTED}>People who opted out. Nobody on your team can message them.</p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Reported by</TableHead>
                <TableHead>Added</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(dnc ?? []).length === 0 && <EmptyRow cols={4}>Nobody has opted out.</EmptyRow>}
              {(dnc ?? []).map((d) => (
                <TableRow key={d.profile_key}>
                  <TableCell className="text-sm text-[var(--color-silver-bright)]">
                    {d.linkedin_url ? (
                      <a href={d.linkedin_url} target="_blank" rel="noreferrer" className="hover:underline">
                        {d.profile_key.replace(/^in:/, '')}
                      </a>
                    ) : (
                      d.profile_key.replace(/^(in|id|url):/, '')
                    )}
                  </TableCell>
                  <TableCell className={MUTED}>{d.reason.replace(/_/g, ' ')}</TableCell>
                  <TableCell className={MUTED}>{d.source_name || d.source_email || '-'}</TableCell>
                  <TableCell className={MUTED}>{fmtLocale(d.created_at, 'date', '-')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Compliance export
// ---------------------------------------------------------------------------

/** Server limit (routes/team.ts EXPORT_MAX_DAYS). */
export const EXPORT_MAX_DAYS = 31

export function ExportPage() {
  const today = localDateStamp(new Date())
  const [to, setTo] = useState(today)
  const [from, setFrom] = useState(localDateStamp(new Date(Date.now() - (EXPORT_MAX_DAYS - 1) * 86_400_000)))
  const exportCsv = useExportTeamMessages()
  return (
    <Card className="max-w-xl">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Compliance archive</CardTitle>
        <p className={MUTED}>
          Download every message your reps sent or received as a CSV: time, rep, lead, LinkedIn URL, direction, text and
          campaign. Up to {EXPORT_MAX_DAYS} days per file. Dates are UTC.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <label className="flex flex-1 flex-col gap-1 text-xs text-[var(--color-silver)]">
            From
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-8 text-xs" />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-xs text-[var(--color-silver)]">
            To
            <Input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className="h-8 text-xs" />
          </label>
        </div>
        <Button size="sm" disabled={!from || !to || exportCsv.isPending} onClick={() => exportCsv.mutate({ from, to })}>
          {exportCsv.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
          Download CSV
        </Button>
        {exportCsv.error && <p className="text-xs text-[var(--color-danger)]">{exportCsv.error.message}</p>}
      </CardContent>
    </Card>
  )
}
